import { goalTypeLabel } from "@/shared/domain/agents";
import {
  AgentThreadGoalRecord,
  AgentThreadGoalStatus,
} from "@/shared/domain/agentThread/AgentThreadRecord";
import {
  CheckCircleOutlineIcon,
  ErrorOutlineIcon,
  HourglassEmptyIcon,
} from "@/shared/foundation/icons/mui";

import styles from "./GoalsStrip.module.css";

interface GoalsStripProps {
  goals: AgentThreadGoalRecord[];
}

function chipClassNameForStatus(status: AgentThreadGoalStatus): string {
  switch (status) {
    case AgentThreadGoalStatus.ACHIEVED:
      return `${styles.chip} ${styles.chipAchieved}`;
    case AgentThreadGoalStatus.FAILED:
      return `${styles.chip} ${styles.chipFailed}`;
    case AgentThreadGoalStatus.PENDING:
    default:
      return `${styles.chip} ${styles.chipPending}`;
  }
}

function iconForStatus(status: AgentThreadGoalStatus): React.ReactElement {
  switch (status) {
    case AgentThreadGoalStatus.ACHIEVED:
      return <CheckCircleOutlineIcon className={styles.icon} />;
    case AgentThreadGoalStatus.FAILED:
      return <ErrorOutlineIcon className={styles.icon} />;
    case AgentThreadGoalStatus.PENDING:
    default:
      return <HourglassEmptyIcon className={styles.icon} />;
  }
}

/**
 * Renders the goals declared on a single turn as a horizontal strip of status
 * chips, one per goal. Surfaces what each turn was asked to achieve and
 * whether the agent succeeded.
 */
export function GoalsStrip({ goals }: GoalsStripProps) {
  if (goals.length === 0) {
    return null;
  }

  return (
    <div className={styles.container} aria-label="Declared goals for this turn">
      {goals.map((goal, idx) => {
        const label = goalTypeLabel(goal.goal_type);
        return (
          <span
            key={`${goal.message_sequence_num}-${idx}`}
            className={chipClassNameForStatus(goal.status)}
            title={`${label}: ${goal.status}`}
          >
            {iconForStatus(goal.status)}
            <span className={styles.label}>{label}</span>
          </span>
        );
      })}
    </div>
  );
}
